import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { usePlanActions, isPaid } from "@/lib/planActions";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { QualitySummary } from "@/components/plan/QualitySummary";
import { DayTargets } from "@/components/plan/DayTargets";
import { MealCard } from "@/components/plan/MealCard";
import { AlternativesDialog } from "@/components/plan/AlternativesDialog";
import { PortionsDialog } from "@/components/plan/PortionsDialog";
import { ShareDialog } from "@/components/plan/ShareDialog";
import { toast } from "sonner";
import { RefreshCw, Sparkles, Wand2, Lock, Share2 } from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function MealPlan() {
  const { t } = useLang();
  const { user } = useAuth();
  const qc = useQueryClient();
  const actions = usePlanActions();
  const paid = isPaid(user);
  const [active, setActive] = useState(0);
  const [memberId, setMemberId] = useState("primary");
  const [generating, setGenerating] = useState(false);
  const [alt, setAlt] = useState(null);      // {slot, list, loading}
  const [portionsSlot, setPortionsSlot] = useState(null);
  const [share, setShare] = useState(false);

  const { data } = useQuery({ queryKey: ["mealplan"], queryFn: async () => (await api.get("/meal-plan")).data });
  const plan = data?.exists ? data.plan : null;
  const day = plan?.days?.[active];
  const members = plan?.members || [];
  const currentMember = members.find((m) => m.member_id === memberId) ? memberId : (members[0]?.member_id || "primary");

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/meal-plan/generate");
      await qc.invalidateQueries({ queryKey: ["mealplan"] });
      await qc.invalidateQueries({ queryKey: ["grocery"] });
      toast.success("✓");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setGenerating(false); }
  };

  const openAlternatives = async (slot) => {
    if (!paid) { await actions.alternatives(day.day, slot); return; }
    setAlt({ slot, list: null, loading: true });
    const list = await actions.alternatives(day.day, slot);
    setAlt(list ? { slot, list, loading: false } : null);
  };
  const chooseAlternative = async (recipeId) => {
    const slot = alt.slot;
    setAlt(null);
    await actions.swap(day.day, slot, recipeId);
  };
  const portionsMeal = portionsSlot ? day?.meals?.find((m) => m.slot === portionsSlot) : null;

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-4 chenia-fade-up">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">{t("plan_title")}</h1>
          <p className="text-muted-foreground mt-1">{t("plan_sub")}</p>
        </div>
        {plan && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShare(true)} className="rounded-full" data-testid="plan-share">
              <Share2 className="h-4 w-4 mr-1" /> {t("share_plan")} {!paid && <Lock className="h-3 w-3 ml-1.5 text-muted-foreground" />}
            </Button>
            <Button variant="outline" onClick={actions.optimize} disabled={actions.busy === "optimize"} className="rounded-full" data-testid="plan-optimize">
              <Wand2 className={`h-4 w-4 mr-1 ${actions.busy === "optimize" ? "animate-pulse" : ""}`} /> {actions.busy === "optimize" ? t("optimizing") : t("optimize_week")}
            </Button>
            <Button onClick={generate} disabled={generating} className="rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="plan-regenerate">
              <RefreshCw className={`h-4 w-4 mr-1 ${generating ? "animate-spin" : ""}`} /> {t("regenerate")}
            </Button>
          </div>
        )}
      </div>

      {!plan ? (
        <div className="chenia-card p-8 mt-8 text-center" data-testid="plan-empty">
          <h2 className="text-xl font-semibold">{t("no_plan_title")}</h2>
          <p className="mt-2 text-muted-foreground">{t("no_plan_desc")}</p>
          <Button onClick={generate} disabled={generating} className="mt-5 rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="plan-generate">
            {generating ? (<><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> {t("generating")}</>) : (<><Sparkles className="h-4 w-4 mr-1" /> {t("generate_plan")}</>)}
          </Button>
        </div>
      ) : (
        <>
          {members.length > 1 && (
            <div className="mt-6 flex flex-wrap gap-2 pb-1" data-testid="plan-member-tabs">
              {members.map((m) => (
                <button key={m.member_id} data-testid={`plan-member-${m.member_id}`} onClick={() => setMemberId(m.member_id)}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    currentMember === m.member_id ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:bg-secondary"}`}>
                  {m.name} <span className="opacity-60 text-xs">· {m.targets.calories} kcal</span>
                </button>
              ))}
            </div>
          )}

          <div className="mt-5">
            <QualitySummary plan={plan} memberId={currentMember} />
          </div>

          {/* Day tabs */}
          <div className="mt-6 grid grid-cols-7 gap-1.5 sm:flex sm:gap-2 pb-2" data-testid="plan-day-tabs">
            {DAYS.map((dk, i) => (
              <button key={dk} data-testid={`plan-day-${dk}`} onClick={() => setActive(i)}
                className={`min-w-0 px-1 sm:px-4 py-2.5 rounded-2xl text-xs sm:text-sm font-medium text-center transition-colors ${
                  active === i ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:bg-secondary"}`}>
                {t(dk).slice(0, 3)}
              </button>
            ))}
          </div>

          <div className="mt-4 grid lg:grid-cols-[1fr_auto] gap-3 items-start">
            <DayTargets day={day} memberId={currentMember} />
            <Button variant="outline" onClick={() => actions.regenerateDay(day.day)} disabled={actions.busy === `day-${day.day}`}
              className="rounded-full w-full lg:w-auto" data-testid="plan-regenerate-day">
              <RefreshCw className={`h-4 w-4 mr-1 ${actions.busy === `day-${day.day}` ? "animate-spin" : ""}`} /> {t("regen_day")} {!paid && <Lock className="h-3 w-3 ml-1.5 text-muted-foreground" />}
            </Button>
          </div>

          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            {day?.meals?.map((m) => (
              <MealCard key={m.slot} meal={m} day={day.day} memberId={currentMember} paid={paid} busy={actions.busy}
                onAlternatives={() => openAlternatives(m.slot)}
                onRegenerate={() => actions.swap(day.day, m.slot)}
                onPortions={() => setPortionsSlot(m.slot)} />
            ))}
          </div>

          <ShareDialog open={share} onClose={() => setShare(false)} />
          <AlternativesDialog open={!!alt} onClose={() => setAlt(null)} alternatives={alt?.list} loading={alt?.loading} onChoose={chooseAlternative} />
          <PortionsDialog open={!!portionsMeal} onClose={() => setPortionsSlot(null)} meal={portionsMeal} members={members}
            busy={!!actions.busy} onChange={(mid, p) => actions.setPortions(day.day, portionsMeal.slot, mid, p)} />
        </>
      )}

      <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{t("disclaimer")}</p>
    </AppShell>
  );
}
